import React, { useState } from 'react';
import { SPACE_TYPES, SPACE_TYPE_COLORS, SPACE_CATEGORY_VISIBILITY, SENSOR_TYPES, SENSOR_COLORS } from '../utils/constants';
import './SpaceLegend.css';

/**
 * SpaceLegend Component
 * 
 * Displays a legend for space types and sensor indicators
 * Includes toggles to control visibility of each type
 * 
 * @param {Object} props - Component props
 * @param {Function} props.onToggleSpaceType - Function to toggle visibility of space type
 * @param {Object} props.spaceTypeVisibility - Object tracking visibility of space types
 * @param {Function} props.onToggleSensorType - Function to toggle visibility of sensor type
 * @param {Object} props.sensorTypeVisibility - Object tracking visibility of sensor types
 * @param {Function} props.onToggleCentroid - Function to toggle centroid visibility
 * @param {boolean} props.centroidVisibility - Whether centroids are visible
 * @param {Function} props.onToggleColorBySensorType - Function to toggle coloring by sensor type
 * @param {boolean} props.colorBySensorType - Whether spaces are colored by sensor type
 * @returns {JSX.Element} Legend component
 */
const SpaceLegend = ({ 
  onToggleSpaceType,
  spaceTypeVisibility = SPACE_CATEGORY_VISIBILITY,
  onToggleSensorType,
  sensorTypeVisibility = {},
  onToggleCentroid,
  centroidVisibility = false,
  onToggleColorBySensorType,
  colorBySensorType = false
}) => {
  // State to track if legend is collapsed
  const [isCollapsed, setIsCollapsed] = useState(false);
  
  // Toggle legend visibility
  const toggleLegend = () => {
    setIsCollapsed(!isCollapsed);
  };
  
  /**
   * Creates a darker version of a given color for borders
   * @param {string} color - Hex color code
   * @returns {string} Darker version of the color
   */
  const getDarkerColor = (color) => {
    // If color is not a hex code, return a default dark color
    if (!color || !color.startsWith('#')) return '#333333';
    
    // Convert hex to RGB
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    
    // Make each component darker by multiplying by 0.7
    const darkerR = Math.floor(r * 0.7);
    const darkerG = Math.floor(g * 0.7);
    const darkerB = Math.floor(b * 0.7);
    
    // Convert back to hex
    return `#${darkerR.toString(16).padStart(2, '0')}${darkerG.toString(16).padStart(2, '0')}${darkerB.toString(16).padStart(2, '0')}`;
  };
  
  return (
    <div className={`space-legend ${isCollapsed ? 'collapsed' : ''}`}>
      <div className="legend-header">
        <h3>Legend</h3>
        <button 
          className="legend-toggle-button" 
          onClick={toggleLegend}
          aria-label={isCollapsed ? "Expand legend" : "Collapse legend"}
        >
          {isCollapsed ? '+' : '−'}
        </button>
      </div>
      
      {!isCollapsed && (
        <>
          {/* Space Types Section */}
          <div className="legend-section">
            <h4>Spaces</h4>
            <div className="legend-items">
              <div className="legend-item">
                <label className="toggle-label">
                  <input 
                    type="checkbox" 
                    checked={spaceTypeVisibility[SPACE_TYPES.ROOM]} 
                    onChange={() => onToggleSpaceType && onToggleSpaceType(SPACE_TYPES.ROOM)}
                  />
                  <div className="color-box" style={{ backgroundColor: SPACE_TYPE_COLORS[SPACE_TYPES.ROOM] }}></div>
                  <span>Room</span>
                </label>
              </div>
              <div className="legend-item">
                <label className="toggle-label">
                  <input 
                    type="checkbox" 
                    checked={spaceTypeVisibility[SPACE_TYPES.DESK]} 
                    onChange={() => onToggleSpaceType && onToggleSpaceType(SPACE_TYPES.DESK)}
                  />
                  <div className="color-box" style={{ backgroundColor: SPACE_TYPE_COLORS[SPACE_TYPES.DESK] }}></div>
                  <span>Desk</span>
                </label>
              </div>
              <div className="legend-item">
                <label className="toggle-label">
                  <input 
                    type="checkbox" 
                    checked={spaceTypeVisibility[SPACE_TYPES.PHONE]} 
                    onChange={() => onToggleSpaceType && onToggleSpaceType(SPACE_TYPES.PHONE)}
                  />
                  <div className="color-box" style={{ backgroundColor: SPACE_TYPE_COLORS[SPACE_TYPES.PHONE] }}></div>
                  <span>Phone Booth</span>
                </label>
              </div>
              <div className="legend-item">
                <label className="toggle-label">
                  <input 
                    type="checkbox" 
                    checked={spaceTypeVisibility[SPACE_TYPES.FLEX]} 
                    onChange={() => onToggleSpaceType && onToggleSpaceType(SPACE_TYPES.FLEX)}
                  />
                  <div className="color-box" style={{ backgroundColor: SPACE_TYPE_COLORS[SPACE_TYPES.FLEX] }}></div>
                  <span>Flex</span>
                </label>
              </div>
              <div className="legend-item">
                <label className="toggle-label">
                  <input 
                    type="checkbox" 
                    checked={spaceTypeVisibility[SPACE_TYPES.ENTRY]} 
                    onChange={() => onToggleSpaceType && onToggleSpaceType(SPACE_TYPES.ENTRY)}
                  />
                  <div className="color-box" style={{ backgroundColor: SPACE_TYPE_COLORS[SPACE_TYPES.ENTRY] }}></div>
                  <span>Entry</span>
                </label>
              </div>
            </div>
          </div>
          
          {/* Sensor Types Section */}
          <div className="legend-section">
            <h4>Sensors</h4>
            <div className="legend-items">
              <div className="legend-item">
                <label className="toggle-label">
                  <input 
                    type="checkbox" 
                    checked={sensorTypeVisibility[SENSOR_TYPES.OA] !== false} 
                    onChange={() => onToggleSensorType && onToggleSensorType(SENSOR_TYPES.OA)}
                  />
                  <div className="sensor-square" style={{ 
                    border: `1px solid ${SPACE_TYPE_COLORS[SPACE_TYPES.ROOM]}`,
                    width: '10px', 
                    height: '10px'
                  }}></div>
                  <span>OA</span>
                </label>
              </div>
              <div className="legend-item">
                <label className="toggle-label">
                  <input 
                    type="checkbox" 
                    checked={sensorTypeVisibility[SENSOR_TYPES.WAFFLE] !== false} 
                    onChange={() => onToggleSensorType && onToggleSensorType(SENSOR_TYPES.WAFFLE)}
                  />
                  <div className="sensor-circle" style={{ 
                    border: `1px solid ${SPACE_TYPE_COLORS[SPACE_TYPES.FLEX]}`,
                    width: '10px', 
                    height: '10px'
                  }}></div>
                  <span>Waffle</span>
                </label>
              </div>
              <div className="legend-item">
                <label className="toggle-label">
                  <input 
                    type="checkbox" 
                    checked={sensorTypeVisibility[SENSOR_TYPES.ENTRY] !== false} 
                    onChange={() => onToggleSensorType && onToggleSensorType(SENSOR_TYPES.ENTRY)}
                  />
                  <div className="sensor-square" style={{ 
                    backgroundColor: SENSOR_COLORS[SENSOR_TYPES.ENTRY],
                    border: `1px solid ${getDarkerColor(SENSOR_COLORS[SENSOR_TYPES.ENTRY])}`,
                    width: '10px',
                    height: '10px'
                  }}></div>
                  <span>Entry</span>
                </label>
              </div>
            </div>
          </div>
          
          {/* Centroid Toggle Section */}
          <div className="legend-section">
            <h4>Display Options</h4>
            <div className="legend-items">
              <div className="legend-item">
                <label className="toggle-label">
                  <input 
                    type="checkbox" 
                    checked={centroidVisibility} 
                    onChange={() => onToggleCentroid && onToggleCentroid()}
                  />
                  <span>Show Sensors</span>
                </label>
              </div>
              <div className="legend-item">
                <label className="toggle-label">
                  <input 
                    type="checkbox" 
                    checked={colorBySensorType} 
                    onChange={() => onToggleColorBySensorType && onToggleColorBySensorType()}
                  />
                  <span>Color by sensor type</span>
                </label>
                {/* Show sensor color indicators when toggle is active */}
                {colorBySensorType && (
                  <div style={{ marginTop: '6px', marginLeft: '24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                      <div className="sensor-color-indicator" style={{ 
                        backgroundColor: SENSOR_COLORS[SENSOR_TYPES.OA],
                        width: '10px',
                        height: '10px'
                      }}></div>
                      <span style={{ fontSize: '12px' }}>OA Sensors</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div className="sensor-color-indicator" style={{ 
                        backgroundColor: SENSOR_COLORS[SENSOR_TYPES.WAFFLE],
                        width: '10px',
                        height: '10px'
                      }}></div>
                      <span style={{ fontSize: '12px' }}>Waffle Sensors</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SpaceLegend; 